import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { Box } from '@mui/system';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Document, pdfjs, Page as Pages } from 'react-pdf';
import { HiOutlineArrowNarrowLeft, HiCheck } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { PreviewImageApi } from '../../../../store/Slice/Global/GlobalSlice';
import useLoginSelector from '../../../../store/Slice/Login/LoginSelector';
import AgreementService from '../../../services/AgreementService';

const { AgreementSetActive } = AgreementService;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: 12,
  padding: '6px',
  height: '42.5px',
  fontWeight: 500,
  textTransform: 'uppercase',
  color: '#0d4689'
}));

export default function AggrementAddStep5({ handlePrev, reviewData }) {
  const [agreementId, setAgreementId] = useState('');
  const [previewFileType, setPreviewFileType] = useState('image');
  const [openPreview, setOpenPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pageNumber, setPageNumber] = useState(1); // setting 1 to show fisrt page
  const navigate = useNavigate();
  const { LoginData } = useLoginSelector();
  const dispatch = useDispatch();

  useEffect(() => {
    const localAgreement = JSON.parse(localStorage.getItem('agreementId'));
    if (localAgreement) {
      setAgreementId(localAgreement.agreementId || localAgreement.data);
    }
  }, []);

  const handleFinish = async () => {
    await AgreementSetActive(
      {
        aggrementId: agreementId,
        companyId: LoginData?.companyId,
        userId: LoginData?.userId
      },
      LoginData?.jwt
    );

    localStorage.removeItem('reviewData');
    localStorage.removeItem('agreementId');
    localStorage.removeItem('commisssionType');
    navigate('/agreement');
  };

  const handleDocumentPreview = (docType) => {
    const document = reviewData?.updatedReviewData?.find(
      (doc) => doc.documentType === docType.value
    );
  };

  async function handlePreviewDoc(docInfo) {
    const { agreementId, companyId, documentTypeId } = docInfo.data;

    // console.log('Previewing document with:', docInfo);

    const blob = await dispatch(
      PreviewImageApi(
        `/agreement/agrdoc/${companyId}/${agreementId}/${documentTypeId}`,
        null,
        true // 👈 blob mode
      )
    );

    if (!blob) return;

    setPreviewFileType(blob.type === 'application/pdf' ? 'pdf' : 'image');
    const fileURL = URL.createObjectURL(blob);
    setPreviewUrl(fileURL);
    setOpenPreview(true);
  }

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setPageNumber(1);
  }, []);

  return (
    <Box mt={3} mb={3}>
      <Typography variant="h5" mb={3} fontWeight="bold">
        Review Agreement Details
      </Typography>

      {/* Bank Details */}
      <Typography variant="body1" className="headingLineUp headingLineUpOut">
        Bank Details
      </Typography>
      <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography className="reviewLabel">Bank Name</Typography>
            <Typography className="reviewValue">
              {reviewData?.bankDetails?.bankName?.bankName?.label || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography className="reviewLabel">PAN Number</Typography>
            <Typography className="reviewValue">
              {reviewData?.bankDetails?.panNumber || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography className="reviewLabel">GSTIN</Typography>
            <Typography className="reviewValue">
              {reviewData?.bankDetails?.gstin || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography className="reviewLabel">Commission Type</Typography>
            <Typography className="reviewValue">
              {reviewData?.bankDetails?.bankCommissionTypeName?.label || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography className="reviewLabel">Turnover Benefit</Typography>
            <Typography className="reviewValue">
              {reviewData?.bankDetails?.turnoverBenefit ? 'Yes' : 'No'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography className="reviewLabel">Login Code</Typography>
            <Typography className="reviewValue">
              {reviewData?.bankDetails?.loginCode || 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Branch Details */}
      <Typography variant="body1" className="headingLineUp headingLineUpOut">
        Branch Details
      </Typography>
      <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
        {reviewData?.branchDetails?.length > 0 ? (
          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Branch Name</StyledTableCell>
                  <StyledTableCell>City</StyledTableCell>
                  <StyledTableCell>Contact Person</StyledTableCell>
                  <StyledTableCell>Address</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reviewData.branchDetails.map((branch, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{branch.branchName}</TableCell>
                    <TableCell>{branch.cityName}</TableCell>
                    <TableCell>{branch.contactPerson}</TableCell>
                    <TableCell>{branch.branchAddress}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="subtitle2">No branches added.</Typography>
        )}
      </Box>

      {/* Branch Details */}
      <Typography variant="body1" className="headingLineUp headingLineUpOut">
        Uploaded Documents
      </Typography>
      <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
        {reviewData?.documents?.length > 0 ? (
          <Grid container spacing={3}>
            {reviewData.documents.map((docType, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  onClick={() => handlePreviewDoc(docType)}
                  sx={{
                    border: '1px solid #e6e6e6',
                    borderRadius: '12px',
                    padding: 2,
                    height: 120,
                    cursor: 'pointer',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    transition: 'all 0.25s ease',
                    background: '#fafafa',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                      transform: 'translateY(-3px)',
                      background: '#ffffff'
                    }
                  }}
                >
                  {/* Preview icon */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      background: '#0284c7',
                      color: '#fff',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <VisibilityIcon sx={{ fontSize: 18 }} />
                  </Box>

                  {/* File icon */}
                  <Box sx={{ fontSize: 36, mb: 1 }}>📄</Box>

                  {/* Document Name */}
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: '#333',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%'
                    }}
                  >
                    {docType?.documentName}
                  </Typography>

                  {/* File name */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 11,
                      color: '#777',
                      mt: 0.5,
                      textAlign: 'center'
                    }}
                  >
                    {docType?.fileName}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="subtitle2">No documents uploaded.</Typography>
        )}
      </Box>

      {/* Commission Details */}
      <Typography variant="body1" className="headingLineUp headingLineUpOut">
        Commission Details
      </Typography>
      <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
        {reviewData?.commissionDetails?.individual?.length > 0 && (
          <>
            <Typography variant="subtitle2" mb={1}>
              Individual Commission
            </Typography>
            <TableContainer sx={{ minWidth: 800 }} mb={3}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Loan Type</StyledTableCell>
                    <StyledTableCell>Branch</StyledTableCell>
                    <StyledTableCell>Commission %</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reviewData.commissionDetails.individual.map((comm, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{JSON.parse(comm.loanType).loanName}</TableCell>
                      <TableCell>{JSON.parse(comm.branch).branchName}</TableCell>
                      <TableCell>{comm.commission}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {reviewData?.commissionDetails?.volume?.length > 0 && (
          <>
            <Typography variant="subtitle2" mb={1}>
              Volume Commission
            </Typography>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Loan Type</StyledTableCell>
                    <StyledTableCell>Branch</StyledTableCell>
                    <StyledTableCell>Duration</StyledTableCell>
                    <StyledTableCell>Commission %</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reviewData.commissionDetails.volume.map((comm, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{JSON.parse(comm.loanType).loanName}</TableCell>
                      <TableCell>{JSON.parse(comm.branch).branchName}</TableCell>
                      <TableCell>{comm.duration} Days</TableCell>
                      <TableCell>{comm.commission}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {!reviewData?.commissionDetails?.individual?.length &&
          !reviewData?.commissionDetails?.volume?.length && (
            <Typography variant="subtitle2">No commissions added.</Typography>
          )}
      </Box>

      {/* Billing Details */}
      <Typography variant="body1" className="headingLineUp headingLineUpOut">
        Billing Details
      </Typography>
      {/* {console.log('reviewData222', reviewData)} */}
      <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography className="reviewLabel">Chargeback Enabled</Typography>
            <Typography className="reviewValue">
              {reviewData?.billingDetails?.chargebackEnabled ? 'Yes' : 'No'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography className="reviewLabel">Billing Cycle Day</Typography>
            <Typography className="reviewValue">
              {reviewData?.billingDetails?.billingCycleDay || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography className="reviewLabel">TDS (%)</Typography>
            <Typography className="reviewValue">
              {reviewData?.billingDetails?.tdsPercent || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography className="reviewLabel">GST (%)</Typography>
            <Typography className="reviewValue">
              {reviewData?.billingDetails?.gstPercent || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography className="reviewLabel">Invoice Mode</Typography>
            <Typography className="reviewValue">
              {reviewData?.billingDetails?.invoiceMode || 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          onClick={handlePrev}
          startIcon={<HiOutlineArrowNarrowLeft />}
          className="mainBtn previousBtn"
          variant="outlined"
        >
          Back
        </Button>

        <Button
          onClick={handleFinish}
          startIcon={<HiCheck />}
          className="mainBtn nextBtn"
          variant="contained"
          sx={{ ml: 'auto' }}
        >
          Finish & Activate
        </Button>
      </Box>
      <Dialog
        open={openPreview}
        onClose={() => {
          setOpenPreview(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Document Preview</span>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'auto'
          }}
        >
          {previewUrl && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                width: '100%'
              }}
            >
              {previewFileType === 'pdf' ? (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    bgcolor: '#f5f5f5',
                    p: 2,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: '70vh'
                  }}
                >
                  <Document
                    file={previewUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<div>Loading PDF...</div>}
                  >
                    <Pages
                      pageNumber={pageNumber}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </Box>
              ) : (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Preview"
                  sx={{
                    width: '100%',
                    maxHeight: 500,
                    objectFit: 'contain',
                    transition: 'transform 0.3s ease-in-out',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                      cursor: 'zoom-in'
                    }
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpenPreview(false);
            }}
            variant="contained"
            sx={{
              bgcolor: '#0284c7',
              '&:hover': { bgcolor: '#0369a1' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
